import { MiniGameBase } from './base.js';

/**
 * "Put in Order" sequential mini-game
 * Player arranges items in correct order
 */
export class PutInOrderGame extends MiniGameBase {
    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        super(deepDiveData, categoryName, onComplete, category);
        this.currentOrder = this.shuffleItems();
        this.completed = false;
        this.draggedElement = null;
    }

    /**
     * Shuffle items for initial display
     */
    shuffleItems() {
        const items = [...this.data.items];
        return this.shuffleArray(items);
    }

    /**
     * Render the ordering UI
     */
    render(container) {
        super.render(container);

        container.innerHTML = `
            <div class="deepdive-container">
                <div class="instruction">${this.data.instruction || 'Put these in order:'}</div>
                ${this.data.orderingCriterion ?
                    `<div class="ordering-hint">Ordering by: ${this.data.orderingCriterion}</div>`
                    : ''}
                <div class="ordering-container">
                    <div class="ordering-list">
                        ${this.currentOrder.map((item, idx) => `
                            <div class="ordering-item" draggable="true" data-index="${idx}">
                                <span>${item}</span>
                            </div>
                        `).join('')}
                    </div>
                </div>
                <div class="button-row">
                    <button class="submit-button">Check Order</button>
                </div>
                <div class="feedback-message" style="display: none;"></div>
            </div>
        `;

        // Add skip button before submit button
        const buttonRow = container.querySelector('.button-row');
        const submitBtn = buttonRow.querySelector('.submit-button');
        const skipBtn = this.createSkipButton();
        buttonRow.insertBefore(skipBtn, submitBtn);

        this.attachEventListeners();
    }

    /**
     * Attach drag-and-drop and button listeners
     */
    attachEventListeners() {
        const items = this.container.querySelectorAll('.ordering-item');
        const submitButton = this.container.querySelector('.submit-button');

        // Drag and drop (desktop)
        items.forEach((item, idx) => {
            item.addEventListener('dragstart', (e) => this.handleDragStart(e, idx));
            item.addEventListener('dragover', (e) => this.handleDragOver(e));
            item.addEventListener('drop', (e) => this.handleDrop(e, idx));
            item.addEventListener('dragend', (e) => this.handleDragEnd(e));

            // Touch support (mobile)
            this.setupTouchDrag(
                item,
                () => this.playSelectSound(),
                (dragIdx, dropIdx) => this.handleTouchDrop(dragIdx, dropIdx),
                '.ordering-item',
                idx
            );

            // Attach hint if this item matches a word in category
            const itemText = this.currentOrder[idx];
            if (itemText) {
                this.attachHintIfExists(item, itemText);
            }
        });

        // Submit button
        submitButton.addEventListener('click', () => this.handleSubmit());
    }

    /**
     * Handle touch drop
     */
    handleTouchDrop(dragIndex, dropIndex) {
        if (dragIndex === dropIndex) return;

        // Reorder items
        const draggedItem = this.currentOrder[dragIndex];
        this.currentOrder.splice(dragIndex, 1);
        this.currentOrder.splice(dropIndex, 0, draggedItem);

        // Re-render
        this.render(this.container);
    }

    /**
     * Handle drag start
     */
    handleDragStart(e, index) {
        this.playSelectSound();
        this.draggedElement = index;
        e.target.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
    }

    /**
     * Handle drag over
     */
    handleDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        e.currentTarget.classList.add('drag-over');
    }

    /**
     * Handle drop
     */
    handleDrop(e, dropIndex) {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');

        if (this.draggedElement === null || this.draggedElement === dropIndex) {
            return;
        }

        // Swap items in currentOrder
        const draggedItem = this.currentOrder[this.draggedElement];
        this.currentOrder.splice(this.draggedElement, 1);
        this.currentOrder.splice(dropIndex, 0, draggedItem);

        // Re-render
        this.render(this.container);
    }

    /**
     * Handle drag end
     */
    handleDragEnd(e) {
        e.target.classList.remove('dragging');

        // Remove drag-over class from all items
        const items = this.container.querySelectorAll('.ordering-item');
        items.forEach(item => item.classList.remove('drag-over'));

        this.draggedElement = null;
    }

    /**
     * Handle submit button click
     */
    handleSubmit() {
        const isCorrect = this.checkAnswer();
        const feedback = this.container.querySelector('.feedback-message');
        const submitButton = this.container.querySelector('.submit-button');

        feedback.style.display = 'block';

        if (isCorrect) {
            this.playMatchSound();
            feedback.className = 'feedback-message success';
            feedback.textContent = 'Perfect Order!';
            this.completed = true;
            submitButton.disabled = true;

            // Disable dragging
            const items = this.container.querySelectorAll('.ordering-item');
            items.forEach(item => {
                item.draggable = false;
                item.style.cursor = 'default';
            });

            setTimeout(() => this.showCelebration(), 1000);
        } else {
            this.playErrorSound();
            feedback.className = 'feedback-message error';
            feedback.textContent = 'Not quite right. Try again!';

            // Shake animation
            const list = this.container.querySelector('.ordering-list');
            list.classList.add('error');
            setTimeout(() => {
                list.classList.remove('error');
                feedback.style.display = 'none';
            }, 2000);
        }

        return { action: isCorrect ? 'correct' : 'incorrect' };
    }

    /**
     * Check if current order matches correct order
     */
    checkAnswer() {
        if (!this.data.correctOrder || this.currentOrder.length !== this.data.correctOrder.length) {
            return false;
        }

        return this.currentOrder.every((item, idx) => item === this.data.correctOrder[idx]);
    }

    /**
     * Check if game is complete
     */
    isComplete() {
        return this.completed;
    }

    /**
     * Handle generic input (for testing)
     */
    handleInput(selection) {
        if ('reorder' in selection) {
            this.currentOrder = selection.reorder;
            this.render(this.container);
            return { action: 'reordered' };
        }
        return { action: 'none' };
    }
}
